import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

/**
 * Simple Tooltip Component
 * 
 * A lightweight, accessible tooltip component for showing additional information.
 * Works on both desktop (hover) and mobile (tap).
 * 
 * This is a simpler alternative to the Radix UI Tooltip that doesn't require
 * a TooltipProvider wrapper and has built-in mobile support.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The element that triggers the tooltip
 * @param {React.ReactNode} props.content - Tooltip content (text or JSX)
 * @param {string} [props.side] - Position relative to trigger ('top' | 'bottom' | 'left' | 'right')
 * @param {number} [props.delay] - Delay before showing tooltip in ms (default: 200)
 * @param {string} [props.className] - Additional CSS classes for tooltip
 * @param {boolean} [props.disabled] - Disable tooltip
 * 
 * @example
 * // Basic tooltip
 * <SimpleTooltip content="This is verified by the community">
 *   <CheckCircleIcon className="w-4 h-4 text-green-600" />
 * </SimpleTooltip>
 * 
 * @example
 * // Tooltip on different side
 * <SimpleTooltip content="Left side" side="left">
 *   <Button>Hover me</Button>
 * </SimpleTooltip>
 * 
 * @example
 * // Rich content tooltip
 * <SimpleTooltip 
 *   content={
 *     <div>
 *       <strong>Community Verified</strong>
 *       <br />
 *       High confidence (95%)
 *     </div>
 *   }
 * >
 *   <Badge>Details</Badge>
 * </SimpleTooltip>
 */
export function SimpleTooltip({
  children,
  content,
  side = 'top',
  delay = 200,
  className,
  disabled = false,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const timeoutRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    // Detect mobile devices
    const checkMobile = () => {
      setIsMobile(
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      );
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Close tooltip when clicking outside (mobile)
  useEffect(() => {
    if (!isVisible || !isMobile) return;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isVisible, isMobile]);

  const showTooltip = () => {
    if (disabled) return;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // No delay on mobile for better UX
    const tooltipDelay = isMobile ? 0 : delay;
    
    timeoutRef.current = window.setTimeout(() => {
      setIsVisible(true);
    }, tooltipDelay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
  };

  const handleClick = (e) => {
    if (disabled) return;
    
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();
      setIsVisible(!isVisible);
    }
  };

  const handleFocus = () => {
    if (!disabled && !isMobile) {
      showTooltip();
    }
  };

  const handleBlur = () => {
    if (!isMobile) {
      hideTooltip();
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Position classes
  const positions = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  // Arrow classes
  const arrows = {
    top: 'top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 -mb-px border-4 border-transparent border-b-gray-900',
    left: 'left-full top-1/2 -translate-y-1/2 -ml-px border-4 border-transparent border-l-gray-900',
    right: 'right-full top-1/2 -translate-y-1/2 -mr-px border-4 border-transparent border-r-gray-900',
  };

  if (disabled || !content) {
    return <>{children}</>;
  }

  return (
    <div
      ref={containerRef}
      className="relative inline-flex"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={handleClick}
    >
      {children}

      {isVisible && (
        <div
          role="tooltip"
          className={cn(
            'absolute z-50 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg',
            'pointer-events-none',
            'tooltip-animate',
            positions[side],
            // Only apply whitespace-nowrap if className doesn't override it
            (!className || (!className.includes('whitespace-normal') && !className.includes('whitespace-pre'))) && 'whitespace-nowrap',
            className
          )}
        >
          {content}
          <div className={cn('absolute', arrows[side])} />
        </div>
      )}
    </div>
  );
}

/**
 * Simple Tooltip with group-based hover (CSS-only alternative)
 * 
 * Use this for simpler cases where you don't need JavaScript state management.
 * Just add 'group' class to parent and use this component.
 * 
 * @example
 * <div className="relative inline-flex group">
 *   <SimpleTooltipGroup content="Hover me">
 *     <Button>Button</Button>
 *   </SimpleTooltipGroup>
 * </div>
 */
export function SimpleTooltipGroup({ children, content, side = 'top', className }) {
  const positions = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const arrows = {
    top: 'top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 -mb-px border-4 border-transparent border-b-gray-900',
    left: 'left-full top-1/2 -translate-y-1/2 -ml-px border-4 border-transparent border-l-gray-900',
    right: 'right-full top-1/2 -translate-y-1/2 -mr-px border-4 border-transparent border-r-gray-900',
  };

  return (
    <>
      {children}
      <div
        role="tooltip"
        className={cn(
          'absolute z-50 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg',
          'whitespace-nowrap pointer-events-none',
          'opacity-0 group-hover:opacity-100 group-focus:opacity-100',
          'transition-opacity duration-200',
          positions[side],
          className
        )}
      >
        {content}
        <div className={cn('absolute', arrows[side])} />
      </div>
    </>
  );
}

export default SimpleTooltip;

